"use client"

import { useState, useEffect } from "react"
import { X, Sparkles, Brain, CheckCircle2, ArrowRight, Lightbulb } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

interface FirstImpression {
  field: string
  value: string
  section: string
}

interface FirstImpressionsCardProps {
  impressions?: FirstImpression[]
  savedFields?: FirstImpression[]
  memoryCount?: number
  onClose: () => void
  onContinue?: () => void
  onViewBrain?: () => void
  className?: string
}

export function FirstImpressionsCard({
  impressions,
  savedFields,
  memoryCount,
  onClose,
  onContinue,
  onViewBrain,
  className,
}: FirstImpressionsCardProps) {
  const displayImpressions = savedFields || impressions || []
  const displayMemoryCount = memoryCount ?? displayImpressions.length

  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 300)
  }

  // Group impressions by section
  const groupedImpressions = displayImpressions.reduce(
    (acc, imp) => {
      if (!acc[imp.section]) acc[imp.section] = []
      acc[imp.section].push(imp)
      return acc
    },
    {} as Record<string, FirstImpression[]>,
  )

  const sectionLabels: Record<string, string> = {
    identity: "Business Identity",
    "mission-vision": "Mission & Vision",
    offerings: "Products & Services",
    audience: "Target Audience",
    finances: "Financial Profile",
    goals: "Goals & Challenges",
  }

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 transition-opacity duration-300",
        isVisible ? "opacity-100" : "opacity-0",
        className,
      )}
    >
      <div
        className={cn(
          "w-full max-w-lg bg-surface-elevated rounded-2xl shadow-2xl overflow-hidden transition-all duration-300",
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-4",
        )}
      >
        {/* Header with gradient */}
        <div className="relative px-6 py-5 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">First Impressions</h2>
              <p className="text-sm text-muted-foreground">Here's what BizzIQ learned about you</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Summary - use displayMemoryCount */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
            <Brain className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground leading-relaxed">
                I've saved <span className="font-semibold text-primary">{displayMemoryCount} memories</span> about your
                business. The more you share, the smarter my advice becomes.
              </p>
            </div>
          </div>

          {/* Key Learnings - use displayImpressions */}
          <div className="space-y-3">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              <CheckCircle2 className="h-4 w-4 text-primary" />
              What I Learned
              <span className="text-xs text-muted-foreground">({displayImpressions.length} items)</span>
            </button>

            {showDetails && (
              <div className="space-y-3 pl-6 animate-in slide-in-from-top-2 duration-200">
                {Object.entries(groupedImpressions).map(([section, items]) => (
                  <div key={section} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {sectionLabels[section] || section}
                    </p>
                    {items.map((item, idx) => (
                      <div key={idx} className="flex items-baseline gap-2 text-sm">
                        <span className="text-muted-foreground">{item.field}:</span>
                        <span className="text-foreground font-medium truncate">{item.value}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10">
            <Lightbulb className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              <span className="text-amber-400 font-medium">Tip:</span> Keep adding details about your offerings,
              audience, and goals to unlock personalized dashboards.
            </p>
          </div>
        </div>

        {/* Footer - support both onContinue and onViewBrain */}
        <div className="px-6 py-4 bg-card/50 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {12 - displayMemoryCount > 0
              ? `${12 - displayMemoryCount} more memories to unlock dashboards`
              : "Dashboards unlocked!"}
          </p>
          <Button onClick={onViewBrain || onContinue || handleClose} className="gap-2 bg-primary hover:bg-primary/90">
            Continue Building
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
